import React from "react";
import { LoadingButton } from "../../components/formcontrols/imports";
import {
  translateService,
  routingService,
  authService,
  workSpacesService,
  htmlHelperService,
  dateHelperService,
  commonService,
  stateService,
  signService,
} from "../../services/imports";
import { stateEvents, states } from "../../models/states";
import OptimizedComponent from "../../components/optimizedComponent";
import { TextArea } from "../../components/formcontrols/imports";
import ContactPage from "../contact/contact";
import TermsPage from "../terms/terms";
import DocumentViewer from "../../components/documentViewer/documentViewer";
import SignaturesPanel, { signaturesOf } from "./signaturesPanel";
import linkSession from "../../services/linkSessionService";
import ShareBanner from "./shareBanner";
import ShareDialog from "../../components/share/shareDialog";
import DocumentShares from "../../components/share/documentShares";
import { signingChoices } from "./signingMethods";
import SignMenu from "./signMenu";
import { LinkRefusal } from "../signing/hash/documentLink";
import { AppContext } from './../../Context/AppContext';
import { Modal, ModalBody, ModalHeader } from "reactstrap";
import signingService from "../../services/signingService";
import SigningsSection from "./signingsSection";
import SigningAttempts from "./signingAttempts";
import sessionService from "../../services/sessionService";
import { CLOUD_KINDS } from "../../services/signerManagementService";
import { openSigningTools } from "../../services/dashboardModal";
import { CloudResultNotice } from "../../components/signers/cloudNotice";

// Layout of docs/RUNBOOK-document-page.md §3.1 (DP1): preview first, the
// signatures panel beside it from 1200 px, a drawer or bottom sheet below.
const PANEL_COLLAPSED_KEY = "documentPage.panelCollapsed";
// a document being validated (validationTime null): re-read every 5 s, for
// up to 2 minutes, then "refresh later" (share-prompt task 22)
const VALIDATION_POLL_MS = 5000;
const VALIDATION_WATCH_MS = 2 * 60 * 1000;
const OVERLAY_QUERY = "(max-width: 1199.98px)";

const readPanelCollapsed = () => {
  try {
    return window.localStorage.getItem(PANEL_COLLAPSED_KEY) === "1";
  } catch (e) {
    return false;
  }
};

const matchOverlay = () =>
  typeof window.matchMedia === "function" ? window.matchMedia(OVERLAY_QUERY) : null;

export default class ResultPage extends OptimizedComponent {
  constructor(props) {
    super(props);

    this.state = {
      result: null,
      header: "",
      submitLoading: false,
      signLoading: false, // a signing being started (the Sign menu)
      signingOptions: null,
      documentSigners: null,
      readRefusal: null,
      shareOpen: false,
      sharePrefill: null, // a share to share again, from the share list
      sharesVersion: 0, // bumped after a share: the share list reloads
      // who signed (vsys signings-runbook §4), for signingsDocId
      signingsDocId: null,
      signings: null,
      signingsError: false,
      signingScope: null, // ALL | SHARES | OWN, signed in only
      signingSummary: null,
      // the caller's own process holds the document (409 DOCUMENT_BEING_SIGNED)
      ownProcess: null,
      ownProcessBusy: false,
      // the signing tool is not set up yet (SIGNER_NOT_PROVISIONED): { detail, owner }
      notProvisioned: null,
      // the document whose versions include one a cloud signing tool signed
      cloudSignedDocId: null,
      validationSlow: false,
      isEditing: false,
      modalPageType: null,
      panelCollapsed: readPanelCollapsed(),
      panelOpen: false,
      overlayMode: !!matchOverlay()?.matches,
      modalOpen: false,
    };

    this.panelRef = React.createRef();
    this.panelToggleRef = React.createRef();
    this.panelCloseRef = React.createRef();
    this.keyResult = null;
    this.revisionKey = null;
    this.pdfBytes = null;
    this.pdfBytesKey = null;
  }

  static contextType = AppContext;

  goBack = () => {
    if (this.props.goBack) {
      this.props.goBack();
      return;
    }

    // a document link's session: clear this tab's slot only, never sign out
    // (the tab then starts afresh with its Keycloak session, if any)
    if (authService.hasLinkSession()) {
      linkSession.leave();
      return;
    }

    if (authService.isAuthenticated() && authService.isActive())
      routingService.push("dashboard");
    else {
      authService.localSignOut();
      routingService.push("landing");
    }
  };

  downloadFile = (url = null, name) => {
    htmlHelperService.downloadFile(url, name);
  };

  // a refusal shows its page in place of the document (a withdrawn share,
  // a deleted document); a document link's session ends with its own page
  getFile = (id) => {
    this.startValidation(id);
    this.secureSubscription(
      workSpacesService.getDocumentResult(id, { skipCustomErrorHandling: true }).subscribe(
        (res) => {
          const doc = res.data;
          this.setState({
            result: doc,
            readRefusal: null,
            header: doc?.header,
            isEditing: doc?.header?.length <= 0,
          });
          this.loadSigningContext(doc);
          this.loadSignings(doc);
          this.loadCloudSigned(doc);
          this.watchValidation(doc);
        },
        (e) => {
          if (authService.hasLinkSession()) return;
          const type = signService.problemCode(e);
          const status = e && e.response && e.response.status;
          // 400: no such document - a well-formed exposed id no document has,
          // or an old numeric id from a bookmark (Spring's own 400, whose
          // English detail quotes the value and is never shown)
          const reason =
            type === "SHARE_WITHDRAWN" || type === "DOCUMENT_DELETED"
              ? type
              : status === 400
              ? "DOCUMENT_UNKNOWN"
              : "READ_FAILED";
          this.setState({
            readRefusal: { reason, description: signService.problemMessage(e, null) },
          });
        }
      )
    );
  };

  // A free validation (the public form) is validated only once its document
  // is opened (share-prompt task 22): the start, for every document this page
  // opens - through a share link or signed in; for any other document it does
  // nothing. Never in a public view (403 LINK_SCOPE would end its page). Its
  // answer tells nothing; a failure is tried again at the next opening.
  startValidation = (docId) => {
    if (!docId || linkSession.isPublicView()) return;
    this.secureSubscription(workSpacesService.startFreeValidation(docId).subscribe(() => {}, () => {}));
  };

  // While validationTime is null the document is being validated: re-read it
  // until it is set (a link session has no notifications; signed in, the
  // dashboard's val notification usually comes first), for up to 2 minutes
  watchValidation = (doc) => {
    this.stopValidationWatch();
    this.setState({ validationSlow: false });
    if (!doc || !doc.docExposedId || doc.validationTime != null) return;
    const docId = doc.docExposedId;
    const until = Date.now() + VALIDATION_WATCH_MS;
    this.validationTimer = setInterval(() => {
      const current = this.state.result;
      if (!current || current.docExposedId !== docId || current.validationTime != null) return this.stopValidationWatch();
      if (Date.now() > until) {
        this.stopValidationWatch();
        this.setState({ validationSlow: true });
        return;
      }
      if (document.hidden || this.validationReading) return;
      this.validationReading = true;
      workSpacesService.getDocumentResult(docId, { skipCustomErrorHandling: true }).subscribe(
        (res) => {
          this.validationReading = false;
          const fresh = res.data;
          if (!fresh || fresh.validationTime == null || this.state.result?.docExposedId !== docId) return;
          this.stopValidationWatch();
          this.setState({ result: fresh });
          this.loadSigningContext(fresh);
        },
        () => (this.validationReading = false)
      );
    }, VALIDATION_POLL_MS);
  };

  stopValidationWatch = () => {
    if (this.validationTimer) clearInterval(this.validationTimer);
    this.validationTimer = null;
  };

  // The signing context of the document (share-prompt tasks 11-12):
  // signing-options (rights, hash algorithms, the required signatory) and
  // what signature-gateway says the caller may sign it with (SG6: through a
  // share what it grants, else the caller's own signers and the app-based
  // kinds). A public view signs nothing and calls neither.
  loadSigningContext = (doc) => {
    if (!doc || !doc.docExposedId || linkSession.isPublicView()) return;
    const docId = doc.docExposedId;
    this.setState({ signingDocId: docId, signingOptions: null, documentSigners: null });
    this.secureSubscription(
      workSpacesService.getSigningOptions(docId, { skipCustomErrorHandling: true }).subscribe(
        (res) => this.state.signingDocId === docId && this.setState({ signingOptions: res.data || {} }),
        () => this.state.signingDocId === docId && this.setState({ signingOptions: null })
      )
    );
    this.secureSubscription(
      signService.getDocumentSigners(docId).subscribe(
        (res) => this.state.signingDocId === docId && this.setState({ documentSigners: res.data || null }),
        () => this.state.signingDocId === docId && this.setState({ documentSigners: null })
      )
    );
  };

  // Who signed (vsys docs/signings-runbook.md §4): /signings when the read
  // allows it (actionRights.signingList: never a public view, which a 403
  // would end). Signed in, the caller's scope too, and the counts and the
  // people where the share list or the uploader's scope (ALL) allows them.
  // Read when the document opens or changes; /signings is never polled.
  loadSignings = (doc) => {
    const docId = doc && doc.docExposedId;
    const rights = (doc && doc.actionRights) || {};
    const allowed = !!docId && !!rights.signingList;
    this.setState({
      signingsDocId: allowed ? docId : null,
      signings: null,
      signingsError: false,
      signingScope: null,
      signingSummary: null,
    });
    if (!allowed) return;
    const current = () => this.state.signingsDocId === docId;
    this.secureSubscription(
      signingService.list(docId).subscribe(
        (res) => current() && this.setState({ signings: (res.data && res.data.signings) || [] }),
        () => current() && this.setState({ signingsError: true })
      )
    );
    if (authService.hasLinkSession()) return;
    if (rights.shareList) this.loadSigningSummary(docId);
    this.secureSubscription(
      signingService.scope(docId).subscribe(
        (res) => {
          if (!current()) return;
          const scope = (res.data && res.data.scope) || null;
          this.setState({ signingScope: scope });
          if (scope === "ALL" && !rights.shareList) this.loadSigningSummary(docId);
        },
        () => {}
      )
    );
  };

  // Whether a cloud signing tool signed a version of this document: the
  // validation shows such signatures as untrusted for now, which the page
  // says beside the result (the gateway's website prompts, phase A). Only the
  // gateway's listing knows the signing tool - the caller's own processes at
  // least; signed in only (a link is refused).
  loadCloudSigned = (doc) => {
    const docId = doc && doc.docExposedId;
    this.setState({ cloudSignedDocId: null });
    if (!docId || authService.hasLinkSession() || !authService.isAuthenticated()) return;
    this.secureSubscription(
      signService.getSignedDocumentProcesses(docId).subscribe(
        (res) => {
          const processes = (res.data && res.data.processes) || [];
          if (processes.some((p) => CLOUD_KINDS.includes(p.signerKind))) this.setState({ cloudSignedDocId: docId });
        },
        () => {}
      )
    );
  };

  loadSigningSummary = (docId) => {
    this.secureSubscription(
      signingService.summary(docId).subscribe(
        (res) => this.state.signingsDocId === docId && this.setState({ signingSummary: res.data || null }),
        () => this.state.signingsDocId === docId && this.setState({ signingSummary: null })
      )
    );
  };

  // the counts and the people, and the attempts: the share list's readers
  // or the uploader (scope ALL), signed in
  seesSigningSummary = () => {
    const rights = (this.state.result && this.state.result.actionRights) || {};
    return !authService.hasLinkSession() && (!!rights.shareList || this.state.signingScope === "ALL");
  };

  // a share made or withdrawn changes the invitations
  reloadSigningSummary = () => {
    const docId = this.state.signingsDocId;
    if (docId && this.seesSigningSummary()) this.loadSigningSummary(docId);
  };

  // after a decline: the read's share block and signing-options again
  reloadAfterShareChange = () => {
    const id = this.state.result && this.state.result.docExposedId;
    if (!id) return;
    this.secureSubscription(
      workSpacesService.getDocumentResult(id, { skipCustomErrorHandling: true }).subscribe((res) => {
        this.setState({ result: res.data });
        this.loadSigningContext(res.data);
      })
    );
  };

  // Signing through signature-gateway (task 12): POST /process with a signer's
  // UUID or a kind, the read's workspaceId, the hash of the bytes the viewer
  // shows, then the process page. target: { signerId } | { signerKind }.
  // Errors go to the global error window.
  startGatewaySigning = async (target, preferredAuthType, loadingKey) => {
    const result = this.state.result;
    const options = this.state.signingOptions;
    if (!result || !result.docExposedId || !target || this.state[loadingKey]) return;
    const fail = (message) => {
      if (message) stateService.event(stateEvents.globalError, message);
      this.setState({ [loadingKey]: false });
    };
    this.setState({ [loadingKey]: true });

    const algorithm = signService.pickHashAlgorithm(options && options.allowedClientHashAlgorithms);
    if (!algorithm) return fail(translateService.t("faceSign.noHashAlgorithm"));

    let clientHash;
    try {
      const bytes =
        this.pdfBytes && this.pdfBytesKey === this.getRevisionKey()
          ? this.pdfBytes
          : await htmlHelperService.getPdfBytes(result.docPath);
      clientHash = await signService.digestHex(algorithm, bytes);
    } catch (e) {
      return fail(translateService.t("faceSign.documentError"));
    }

    const body = {
      docId: result.docExposedId,
      workspaceId: result.workspaceExposedId,
      ...target,
      clientHash,
      clientHashAlgorithm: algorithm,
    };
    if (preferredAuthType) body.preferredAuthType = preferredAuthType;
    this.secureSubscription(
      signService.createProcess(body).subscribe(
        (res) => {
          this.setState({ [loadingKey]: false });
          const processId = res.data && res.data.processId;
          if (!processId) return fail(translateService.t("faceSign.startError"));
          stateService.event(stateEvents.openModalPage, null);
          // the process page reads the process (and its ETag) itself, places
          // the signature and, for eID, hands out the Unitool link
          routingService.push(routingService.routeNames.signProcess, { params: { processId } });
        },
        (e) => {
          // one signing at a time: the gateway's message; for the caller's
          // own process (processId) the choice to continue or start again
          const body = (e && e.response && e.response.data) || {};
          // the signing tool is not set up yet: its card for the owner, the
          // advice to ask the owner for anyone else
          if (signService.problemCode(e) === "SIGNER_NOT_PROVISIONED") {
            this.setState({
              [loadingKey]: false,
              notProvisioned: {
                detail: signService.problemMessage(e, ""),
                owner: !authService.hasLinkSession() && sessionService.owns(result.workspaceExposedId),
              },
            });
            return;
          }
          if (signService.problemCode(e) === "DOCUMENT_BEING_SIGNED" && body.processId) {
            this.setState({
              [loadingKey]: false,
              ownProcess: {
                processId: body.processId,
                message: signService.problemMessage(e, null),
                retry: () => this.startGatewaySigning(target, preferredAuthType, loadingKey),
              },
            });
            return;
          }
          fail(signService.problemMessage(e, translateService.t("faceSign.startError")));
        }
      )
    );
  };

  continueOwnProcess = () => {
    const own = this.state.ownProcess;
    if (!own) return;
    this.setState({ ownProcess: null });
    stateService.event(stateEvents.openModalPage, null);
    routingService.push(routingService.routeNames.signProcess, { params: { processId: own.processId } });
  };

  // cancel the caller's own process, then start the signing again
  restartOwnProcess = () => {
    const own = this.state.ownProcess;
    if (!own || this.state.ownProcessBusy) return;
    this.setState({ ownProcessBusy: true });
    this.secureSubscription(
      signService.cancel(own.processId).subscribe(
        () => {
          this.setState({ ownProcess: null, ownProcessBusy: false });
          own.retry();
        },
        (e) => {
          this.setState({ ownProcess: null, ownProcessBusy: false });
          stateService.event(
            stateEvents.globalError,
            signService.problemMessage(e, translateService.t("faceSign.startError"))
          );
        }
      )
    );
  };

  renderNotProvisioned() {
    const np = this.state.notProvisioned;
    if (!np) return null;
    const t = (k, ...a) => translateService.t(`faceSign.${k}`, ...a);
    const close = () => this.setState({ notProvisioned: null });
    return (
      <Modal isOpen={true} fade={false} className="first-time-modal" toggle={close}>
        <ModalHeader>
          <span>{t("notProvisionedPlain")}</span>
        </ModalHeader>
        <ModalBody>
          {np.detail && <p>{np.detail}</p>}
          <p>{t(np.owner ? "notProvisionedOwner" : "notProvisionedMember")}</p>
          <div className="buttons">
            {np.owner && (
              <LoadingButton
                type="button"
                className="button background-color-two color-white"
                onClick={() => {
                  close();
                  openSigningTools();
                }}
              >
                {t("openSigningTools")}
              </LoadingButton>
            )}
            <LoadingButton type="button" className="button background-color-one color-white" onClick={close}>
              {translateService.t("common.close")}
            </LoadingButton>
          </div>
        </ModalBody>
      </Modal>
    );
  }

  renderOwnProcess() {
    const own = this.state.ownProcess;
    if (!own) return null;
    const busy = this.state.ownProcessBusy;
    return (
      <Modal
        isOpen={true}
        fade={false}
        className="first-time-modal"
        toggle={() => !busy && this.setState({ ownProcess: null })}
      >
        <ModalHeader>
          <span>{translateService.t("sign.ownInProgress")}</span>
        </ModalHeader>
        <ModalBody>
          {own.message && <p>{own.message}</p>}
          <div className="buttons">
            <LoadingButton
              type="button"
              className="button background-color-two color-white"
              disabled={busy}
              onClick={this.continueOwnProcess}
            >
              {translateService.t("sign.continue")}
            </LoadingButton>
            <LoadingButton
              type="button"
              className="button background-color-three color-white"
              loading={busy}
              onClick={this.restartOwnProcess}
            >
              {translateService.t("sign.restart")}
            </LoadingButton>
            <LoadingButton
              type="button"
              className="button background-color-one color-white"
              disabled={busy}
              onClick={() => this.setState({ ownProcess: null })}
            >
              {translateService.t("common.cancell")}
            </LoadingButton>
          </div>
        </ModalBody>
      </Modal>
    );
  }

  componentDidMount() {
    if (this.props?.fromRoute) {
      this.secureSubscription(
        stateService.onEvent(stateEvents.openModalPage).subscribe((type) => {
          this.setState((prevState) => {
            let state = { ...prevState };
            state.modalPageType = type;
            return state;
          });
        })
      );
    }

    if (this.props.match?.params.id) {
      this.getFile(this.props.match?.params.id);
    } else {
      this.setState({
        result: this.props.result,
        header: this.props.result?.header,
        isEditing: this.props.result?.header?.length <= 0,
      });
      this.loadSigningContext(this.props.result);
      this.loadSignings(this.props.result);
      this.loadCloudSigned(this.props.result);
      this.startValidation(this.props.result?.docExposedId);
      this.watchValidation(this.props.result);
    }

    this.secureSubscription(
      stateService.getState(states.userInfo).subscribe((data) => {
        this.context.setState({facsimile: data.facsimilePath, useFacsimile: data.useFacsimile});
      })
    );

    this.secureSubscription(
      translateService.subscribe((_) => {
        this.context.setState({currentLang: translateService.key})
      })
    );

    this.overlayQuery = matchOverlay();
    if (this.overlayQuery) {
      if (this.overlayQuery.addEventListener)
        this.overlayQuery.addEventListener("change", this.onOverlayChange);
      else this.overlayQuery.addListener(this.onOverlayChange);
    }

    // reactstrap modals opened elsewhere (processing message, global
    // errors) cover the page through a portal: pause tracking while any is open
    this.onBodyClass();
    this.modalObserver = new MutationObserver(this.onBodyClass);
    this.modalObserver.observe(document.body, { attributes: true, attributeFilter: ["class"] });
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      this.props.result &&
      !commonService.compareObjectValues(prevProps.result, this.props.result)
    ) {
      this.setState({
        result: this.props.result,
        header: this.props.result?.header,
        isEditing: this.props.result?.header?.length <= 0,
      });
      if (!prevProps.result || prevProps.result.docExposedId !== this.props.result.docExposedId) {
        this.loadSigningContext(this.props.result);
        this.loadSignings(this.props.result);
        this.loadCloudSigned(this.props.result);
        this.startValidation(this.props.result.docExposedId);
        this.watchValidation(this.props.result);
      }
    }
  }

  componentWillUnmount() {
    this.stopValidationWatch();
    if (this.modalObserver) this.modalObserver.disconnect();
    if (this.overlayQuery) {
      if (this.overlayQuery.removeEventListener)
        this.overlayQuery.removeEventListener("change", this.onOverlayChange);
      else this.overlayQuery.removeListener(this.onOverlayChange);
    }
    stateService.event(stateEvents.scrollUp);
    this.clearSubscriptions();
    // if(this.props?.fromRoute) {
    //   this.clearSubscriptions();
    // }
  }

  updateHeader(value) {
    this.setState((prevState) => {
      let state = { ...prevState };
      state.result.header = value;

      return { ...prevState, state };
    });
  }

  submit() {
    if (!this.state.header) return;
    this.setState({ submitLoading: true });
    workSpacesService
      .changedocheader(this.state.result?.docExposedId, this.state.header)
      .subscribe(
        (res) => {
          if (res.success) {
            this.props?.onUpdateHeader
              ? this.props.onUpdateHeader(this.state.header)
              : this.updateHeader(this.state.header);

            this.setState({ submitLoading: false, isEditing: false });
          }
        },
        () => {
          this.setState({ submitLoading: false });
        }
      );
  }

  startEditing = () => {
    this.setState({ isEditing: true });
  };

  // ---- panel ---------------------------------------------------------------

  // at desktop widths the panel is always shown: a drawer left open must
  // not reappear by itself when the window narrows again
  onOverlayChange = (e) => {
    this.setState(e.matches ? { overlayMode: true } : { overlayMode: false, panelOpen: false });
  };

  onBodyClass = () => {
    const modalOpen = document.body.classList.contains("modal-open");
    if (modalOpen !== this.state.modalOpen) this.setState({ modalOpen });
  };

  togglePanelCollapsed = () => {
    const panelCollapsed = !this.state.panelCollapsed;
    this.setState({ panelCollapsed });
    try {
      window.localStorage.setItem(PANEL_COLLAPSED_KEY, panelCollapsed ? "1" : "0");
    } catch (e) {
      // blocked storage: the panel just opens expanded next time
    }
  };

  openPanel = () => {
    this.setState({ panelOpen: true }, () => this.panelCloseRef.current?.focus());
  };

  closePanel = () => {
    this.setState({ panelOpen: false }, () => this.panelToggleRef.current?.focus());
  };

  // the drawer and the bottom sheet are modal: Escape closes, Tab stays inside
  onPanelKeyDown = (e) => {
    if (!this.state.panelOpen || !this.state.overlayMode) return;
    if (e.key === "Escape") {
      e.stopPropagation();
      this.closePanel();
      return;
    }
    if (e.key !== "Tab") return;
    const panel = this.panelRef.current;
    if (!panel) return;
    const items = Array.from(
      panel.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => el.getClientRects().length > 0);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && (document.activeElement === first || document.activeElement === panel)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  // ---- viewer --------------------------------------------------------------

  // the revision the viewer shows: a new signature is a new revision; a
  // re-issued pre-signed URL of the same object is not (docs/RUNBOOK-document-page.md §3.2)
  getRevisionKey() {
    const result = this.state.result;
    if (result === this.keyResult) return this.revisionKey;
    const prev = this.keyResult;
    let key = null;
    if (result?.docPath) {
      const base = result.docPath.split("?")[0];
      const count = signaturesOf(result).length;
      if (result.docHash) key = result.docHash;
      // the same object: validation finishing, or a refreshed URL, is not a
      // new revision; a new signature on a validated document is
      else if (
        this.revisionKey &&
        this.keyBase === base &&
        (prev?.processingStatus === 0 || count <= this.keyCount)
      )
        key = this.revisionKey;
      else key = `${base}#${count}`;
      this.keyBase = base;
      this.keyCount = count;
    }
    this.keyResult = result;
    this.revisionKey = key;
    return key;
  }

  // the viewer's Try again: the pre-signed docPath may have expired, so
  // reload the document result and take its fresh docPath (§3.2)
  refreshDocUrl = () =>
    new Promise((resolve, reject) => {
      const id = this.state.result?.docExposedId;
      if (!id) return reject();
      this.secureSubscription(
        workSpacesService.getDocumentResult(id, { skipCustomErrorHandling: true }).subscribe((res) => {
          const docPath = res.data && res.data.docPath;
          if (!docPath) return reject();
          this.setState(
            (prev) => ({ result: { ...prev.result, docPath } }),
            () => resolve(docPath)
          );
        }, reject)
      );
    });

  onViewerBytes = (bytes, revisionKey) => {
    this.pdfBytes = bytes;
    this.pdfBytesKey = revisionKey;
  };

  renderTitle(docHeader, isVisible) {
    return (
      <div className="document-page__title">
        {this.state.result?.shared && (
          <span className="share">
            {translateService.t("result.documentShareStatus")}
            <img src="../assets/images/upArrowYellow.svg" className="up" alt="" />
          </span>
        )}

        {/* the title editor follows the backend's `visible` flag, as before */}
        {((!docHeader || this.state.isEditing) && isVisible) && (
          <div className="inputs df">
            <div className="input-item">
              <TextArea
                name="header"
                value={this.state.header || ""}
                onChange={(e) => {
                  this.setState({
                    header: e.target.value,
                  });
                }}
                autoComplete="off"
                maxLength="120"
                spellCheck="false"
                placeholder={translateService.t("result.docHeaderPlaceholder")}
              />
            </div>
            <div className="buttons">
              <LoadingButton
                className="button background-color-four color-two"
                loading={this.state.submitLoading}
                onClick={() => this.submit()}
              >
                {translateService.t("common.submit")}
              </LoadingButton>
            </div>
          </div>
        )}
        {((docHeader && !this.state.isEditing) || (!isVisible && this.state.header)) && (
          <div className="header-title">
            <span>{this.state.header}</span>
            {isVisible && (
              <button
                type="button"
                className="clickable close"
                onClick={this.startEditing}
                aria-label={translateService.t("document.editTitle")}
                title={translateService.t("document.editTitle")}
              >
                <img src="../../assets/images/close32.svg" alt="" />
              </button>
            )}
          </div>
        )}
        <span className="desc">{this.state.result?.docName}</span>
      </div>
    );
  }

  render() {
    const result = this.state.result;
    let docHeader = '';
    let isVisible = true;

    if (this.props.match?.params.id) {
      docHeader = this.state.result?.header;
      isVisible = this.state.result?.visible;
    } else {
      docHeader = this.props.result?.header;
      isVisible = this.props.result?.visible;
    }
    // the title editor: the caller's own upload only - never through a share
    // or in a document link's session, which the backend refuses even where
    // the read says visible (a free-validation link, an old backfilled share)
    if (authService.hasLinkSession() || result?.share) isVisible = false;

    const hidden = !this.props?.display || !!this.state.modalPageType;
    const overlayOpen = this.state.panelOpen && this.state.overlayMode;
    // tracking counts only what is on screen: pause behind drawers and dialogs
    const viewerPaused = hidden || overlayOpen || this.state.modalOpen;
    const signatureCount = result?.signatureCount ?? signaturesOf(result).length;
    const showViewer = !!result?.docPath && result?.processingStatus != -1;
    // every way to sign here: the Sign menu's choices (never "the first face tool")
    const choices = signingChoices(this.state.signingOptions, this.state.documentSigners);
    // a signer match and no signing button: "none" when the gateway's list
    // (only the sharer's matching signing tools and kinds) is empty - the
    // sharer must add one; "notHere" when it lists one this site cannot use
    // (face signing off in the config, a method not in the browser) - never
    // the sharer's fault. Any choice not disabled is usable.
    const docSigners = this.state.documentSigners;
    const listed = docSigners ? (docSigners.signers || []).length + (docSigners.kinds || []).length : 0;
    const matchProblem =
      !this.state.signingOptions?.signerMatch ||
      !docSigners ||
      choices.some((c) => !c.disabled)
        ? null
        : listed
        ? "notHere"
        : "none";
    // the share dialog: the sharer or the document's owner, never in a link session
    const canShare = !!result?.actionRights?.sharing && !authService.hasLinkSession();
    // the document's shares: by actionRights.shareList alone (the backend
    // decides who may list them; false in link sessions)
    const canListShares = !!result?.actionRights?.shareList && !authService.hasLinkSession();
    // who signed: actionRights.signingList alone; the counts, the people and
    // the attempts: the share list's readers or the uploader, signed in
    const showSignings = !!result?.actionRights?.signingList && this.state.signingsDocId === result?.docExposedId;
    const seesSummary = this.seesSigningSummary();
    const collapsed = this.state.panelCollapsed;

    // a withdrawn share, a deleted document or another refusal of the read
    if (this.state.readRefusal) return <LinkRefusal refusal={this.state.readRefusal} />;

    return (
      <React.Fragment>
        {this.state.modalPageType &&
          this.props?.fromRoute &&
          (this.state.modalPageType === routingService.routeNames.contact ? (
            <ContactPage display={true} />
          ) : this.state.modalPageType === routingService.routeNames.terms ? (
            <TermsPage display={true} />
          ) : null)}
        <div
          className={`view container document-page ${this.props?.isPopup ? "popup" : ""} ${
            hidden ? "d-none" : ""
          }`}
        >
          <button
            type="button"
            className="clickable close"
            onClick={this.goBack}
            aria-label={translateService.t("common.close")}
            title={translateService.t("common.close")}
          >
            <img src="../../assets/images/close32smallInner.svg" alt="" />
          </button>
          <div
            className={`document-page__layout ${collapsed ? "is-panel-collapsed" : ""} ${
              this.state.panelOpen ? "is-panel-open" : ""
            }`}
          >
            <section className="document-page__preview">
              <div className="document-page__header">
                {this.renderTitle(docHeader, isVisible)}
                <div className="document-page__actions">
                  <button
                    type="button"
                    ref={this.panelToggleRef}
                    className="document-page__panel-toggle"
                    aria-expanded={overlayOpen}
                    aria-controls="document-page-panel"
                    onClick={this.openPanel}
                  >
                    {translateService.t("document.signaturesToggle", signatureCount || 0)}
                  </button>
                  <LoadingButton
                    className="button background-color-two color-white download"
                    disabled={!result?.docPath}
                    onClick={() =>
                      result?.docPath && this.downloadFile(result.docPath, result.docName)
                    }
                  >
                    {translateService.t("result.downloadDocument")}
                  </LoadingButton>
                  {/* every way to sign, through the gateway as a process */}
                  <SignMenu
                    choices={choices}
                    loading={this.state.signLoading}
                    disabled={!result?.docPath}
                    onChoose={(c) => this.startGatewaySigning(c.target, c.authType, "signLoading")}
                  />
                  {canShare && (
                    <LoadingButton
                      className="button background-color-one color-white download"
                      onClick={() => this.setState({ shareOpen: true, sharePrefill: null })}
                    >
                      {translateService.t("common.share")}
                    </LoadingButton>
                  )}
                </div>
              </div>
              {result?.share && (
                <ShareBanner
                  docId={result.docExposedId}
                  share={result.share}
                  options={this.state.signingOptions}
                  matchProblem={matchProblem}
                  onChanged={this.reloadAfterShareChange}
                />
              )}
              {canListShares && (
                <DocumentShares
                  docId={result.docExposedId}
                  workspaceId={result.workspaceExposedId}
                  version={this.state.sharesVersion}
                  signingSummary={seesSummary ? this.state.signingSummary : null}
                  onChanged={this.reloadSigningSummary}
                  onShareAgain={(share) => this.setState({ shareOpen: true, sharePrefill: share })}
                />
              )}
              {result?.docExposedId && result.validationTime == null && (
                <p className="document-page__validating" role="status">
                  {translateService.t(this.state.validationSlow ? "document.validatingSlow" : "document.validating")}
                </p>
              )}
              {showViewer ? (
                <DocumentViewer
                  url={result.docPath}
                  revisionKey={this.getRevisionKey()}
                  paused={viewerPaused}
                  onBytes={this.onViewerBytes}
                  refreshUrl={this.refreshDocUrl}
                />
              ) : (
                <div className="document-page__no-preview">
                  {result?.processingStatus === 0 &&
                    translateService.t("result.docIdLoading")}
                </div>
              )}
            </section>
            {this.state.panelOpen && (
              <div
                className="document-page__backdrop"
                onClick={this.closePanel}
                aria-hidden="true"
              />
            )}
            {/* a drawer or sheet below 1200 px is a modal dialog */}
            <div
              id="document-page-panel"
              ref={this.panelRef}
              className="document-page__panel"
              role={overlayOpen ? "dialog" : "complementary"}
              aria-modal={overlayOpen ? "true" : undefined}
              aria-labelledby="document-page-panel-title"
              onKeyDown={this.onPanelKeyDown}
            >
              <div className="document-page__panel-head">
                <h2 id="document-page-panel-title" className="document-page__panel-title">
                  {translateService.t("result.signatures")}
                  {signatureCount ? ` (${signatureCount})` : ""}
                </h2>
                <button
                  type="button"
                  className="document-page__panel-collapse"
                  onClick={this.togglePanelCollapsed}
                  aria-expanded={!collapsed}
                  aria-controls="document-page-panel-body"
                  aria-label={translateService.t(
                    collapsed ? "document.expandPanel" : "document.collapsePanel"
                  )}
                  title={translateService.t(
                    collapsed ? "document.expandPanel" : "document.collapsePanel"
                  )}
                >
                  {collapsed ? "‹" : "›"}
                </button>
                <button
                  type="button"
                  ref={this.panelCloseRef}
                  className="document-page__panel-close"
                  onClick={this.closePanel}
                  aria-label={translateService.t("document.closePanel")}
                  title={translateService.t("document.closePanel")}
                >
                  ×
                </button>
              </div>
              <div id="document-page-panel-body" className="document-page__panel-body">
                {showSignings && (
                  <SigningsSection
                    signings={this.state.signings}
                    error={this.state.signingsError}
                    summary={seesSummary ? this.state.signingSummary : null}
                  />
                )}
                {result?.docExposedId && seesSummary && (
                  <SigningAttempts
                    docId={result.docExposedId}
                    signedIds={(this.state.signings || []).map((s) => s.processId)}
                    canListShares={canListShares}
                    paused={hidden}
                  />
                )}
                {/* a cloud signing tool's signature: shown as untrusted for now */}
                {this.state.cloudSignedDocId === result?.docExposedId && result?.docExposedId && <CloudResultNotice />}
                <SignaturesPanel result={result} onDownload={this.downloadFile} />
              </div>
            </div>
          </div>
        </div>

        {canShare && (
          <ShareDialog
            isOpen={this.state.shareOpen}
            docId={result.docExposedId}
            doc={result}
            prefill={this.state.sharePrefill}
            onShared={() => {
              this.setState((s) => ({ sharesVersion: s.sharesVersion + 1 }));
              this.reloadSigningSummary();
            }}
            onClose={() => this.setState({ shareOpen: false, sharePrefill: null })}
          />
        )}
        {this.renderOwnProcess()}
        {this.renderNotProvisioned()}
      </React.Fragment>
    );
  }
}
